import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Map, AdvancedMarker, InfoWindow, useMap } from '@vis.gl/react-google-maps';
import { MapPin, Navigation, Search, X, User, Building, Mail, Phone, Loader2 } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { getUserContacts, updateLocalContact } from '../../services/userContactsService';
import { getAllPeople, getPeopleByUuid } from '../../services/apiService';
import { geocodeAddress } from '../../services/geocodingService';
import { getCurrentPosition } from '../../services/locationService';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

// Default center (Belgium) - used only as fallback
const DEFAULT_CENTER = { lat: 50.8503, lng: 4.3517 };

// Cabaza logo SVG path (mesh/honeycomb style)
const CABAZA_LOGO_SVG = `
<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" style="stop-color:#8b5cf6;stop-opacity:1" />
      <stop offset="100%" style="stop-color:#7c3aed;stop-opacity:1" />
    </linearGradient>
  </defs>
  <circle cx="20" cy="20" r="18" fill="white" stroke="COLOR" stroke-width="3"/>
  <path d="M20 8 L28 14 L28 26 L20 32 L12 26 L12 14 Z" fill="COLOR" opacity="0.9"/>
  <circle cx="20" cy="20" r="4" fill="white"/>
</svg>
`;

// Get user from localStorage
const getCurrentUser = () => {
    try {
        const userJson = localStorage.getItem('cabaza_user');
        if (userJson) {
            return JSON.parse(userJson);
        }
    } catch (e) {
        console.error('Error parsing user from localStorage:', e);
    }
    return null;
};

/**
 * Custom marker with Cabaza logo
 * @param {Object} profile - Profile/contact data
 * @param {string} markerType - 'cabaza-contact' | 'private-contact' | 'public'
 *   - cabaza-contact: Contact linked to Cabaza profile (purple)
 *   - private-contact: Local contact without Cabaza profile (gray)
 *   - public: Cabaza public profile not in contacts (blue)
 * @param {boolean} isSelected - Whether this marker is selected
 */
const CabazaMarker = ({ profile, markerType = 'public', isSelected, onClick, onShowTooltip, onHideTooltip }) => {
    // Color based on marker type
    const colors = {
        'cabaza-contact': { main: '#8b5cf6', accent: '#7c3aed' },  // Purple - Contact with Cabaza profile
        'private-contact': { main: '#6b7280', accent: '#4b5563' }, // Gray - Local contact without Cabaza profile
        'public': { main: '#3b82f6', accent: '#2563eb' }           // Blue - Public Cabaza profile
    };
    const color = colors[markerType] || colors.public;

    const initials = `${profile.firstName?.[0] || profile.firstname?.[0] || ''}${profile.lastName?.[0] || profile.lastname?.[0] || ''}`.toUpperCase() || '?';
    const fullName = `${profile.firstName || profile.firstname || ''} ${profile.lastName || profile.lastname || ''}`.trim() || 'Profil';

    const handleMouseEnter = (e) => {
        if (onShowTooltip) {
            onShowTooltip({
                position: { x: e.clientX, y: e.clientY },
                name: fullName
            });
        }
    };

    const handleMouseMove = (e) => {
        if (onShowTooltip) {
            onShowTooltip({
                position: { x: e.clientX, y: e.clientY },
                name: fullName
            });
        }
    };

    return (
        <AdvancedMarker
            position={{ lat: parseFloat(profile.latitude), lng: parseFloat(profile.longitude) }}
            onClick={onClick}
        >
            <div
                className="relative cursor-pointer transform transition-transform hover:scale-110"
                onMouseEnter={handleMouseEnter}
                onMouseMove={handleMouseMove}
                onMouseLeave={onHideTooltip}
            >
                <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm shadow-lg border-2 border-white ${isSelected ? 'ring-2 ring-offset-2 ring-cabaza-purple' : ''}`}
                    style={{ backgroundColor: color.main }}
                >
                    <span className="text-white">{initials}</span>
                </div>
                {/* Indicator badge - Cabaza mesh for profiles, user icon for private contacts */}
                <div
                    className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border border-white flex items-center justify-center"
                    style={{ backgroundColor: color.accent }}
                >
                    {markerType === 'private-contact' ? (
                        <svg viewBox="0 0 12 12" className="w-2.5 h-2.5">
                            <circle cx="6" cy="4" r="2.5" fill="white"/>
                            <path d="M2 11 C2 8 4 7 6 7 C8 7 10 8 10 11" fill="white"/>
                        </svg>
                    ) : (
                        <svg viewBox="0 0 12 12" className="w-2.5 h-2.5">
                            <path d="M6 1 L10 4 L10 8 L6 11 L2 8 L2 4 Z" fill="white" opacity="0.9"/>
                        </svg>
                    )}
                </div>
            </div>
        </AdvancedMarker>
    );
};

/**
 * Info window for selected profile
 */
const ProfileInfoWindow = ({ profile, isContact, onClose }) => {
    return (
        <InfoWindow
            position={{ lat: parseFloat(profile.latitude), lng: parseFloat(profile.longitude) }}
            onCloseClick={onClose}
        >
            <div className="p-2 min-w-52">
                <div className="flex items-center gap-3 mb-2">
                    <div
                        className="w-12 h-12 rounded-full flex items-center justify-center font-semibold text-white"
                        style={{ backgroundColor: isContact ? '#8b5cf6' : '#3b82f6' }}
                    >
                        {`${profile.firstName?.[0] || profile.firstname?.[0] || ''}${profile.lastName?.[0] || profile.lastname?.[0] || ''}`}
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-900">
                            {profile.firstName || profile.firstname} {profile.lastName || profile.lastname}
                        </h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isContact ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                            {isContact ? 'Contact' : 'Profil public'}
                        </span>
                    </div>
                </div>

                <div className="space-y-1 text-sm">
                    {(profile.company || profile.COMPANY) && (
                        <div className="flex items-center gap-2 text-gray-600">
                            <Building size={14} />
                            <span>{profile.company || profile.COMPANY}</span>
                        </div>
                    )}
                    {(profile.email || profile.EMAIL) && (
                        <div className="flex items-center gap-2 text-gray-600">
                            <Mail size={14} />
                            <a href={`mailto:${profile.email || profile.EMAIL}`} className="hover:text-blue-500 truncate">
                                {profile.email || profile.EMAIL}
                            </a>
                        </div>
                    )}
                    {(profile.phone || profile.gsm || profile.PHONE || profile.GSM) && (
                        <div className="flex items-center gap-2 text-gray-600">
                            <Phone size={14} />
                            <a href={`tel:${profile.phone || profile.gsm || profile.PHONE || profile.GSM}`} className="hover:text-blue-500">
                                {profile.phone || profile.gsm || profile.PHONE || profile.GSM}
                            </a>
                        </div>
                    )}
                    {(profile.googleAddress || profile.googleaddress || profile.GOOGLEADDRESS) && (
                        <div className="flex items-center gap-2 text-gray-600 mt-2 pt-2 border-t border-gray-200">
                            <MapPin size={14} />
                            <span className="text-xs">{profile.googleAddress || profile.googleaddress || profile.GOOGLEADDRESS}</span>
                        </div>
                    )}
                </div>
            </div>
        </InfoWindow>
    );
};

/**
 * My position marker
 */
const MyPositionMarker = ({ position }) => {
    return (
        <AdvancedMarker position={position}>
            <div className="relative">
                <div className="absolute inset-0 w-8 h-8 bg-green-400 rounded-full animate-ping opacity-30" />
                <div className="w-8 h-8 bg-green-500 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full" />
                </div>
            </div>
        </AdvancedMarker>
    );
};

/**
 * Name tooltip on hover
 */
const NameTooltip = ({ position, name }) => {
    if (!position || !name) return null;

    return (
        <div
            className="fixed z-50 px-3 py-1.5 bg-gray-900 text-white text-sm rounded shadow-lg pointer-events-none"
            style={{ left: position.x + 15, top: position.y - 30 }}
        >
            {name}
        </div>
    );
};

/**
 * Cluster renderer
 */
const ClusterMarker = ({ count, position }) => {
    const size = count < 10 ? 40 : count < 50 ? 50 : 60;

    return (
        <div
            className="flex items-center justify-center rounded-full bg-cabaza-purple text-white font-bold shadow-lg border-3 border-white cursor-pointer hover:scale-110 transition-transform"
            style={{ width: size, height: size, fontSize: count < 100 ? 14 : 12 }}
        >
            {count}
        </div>
    );
};

/**
 * Clustered markers component
 */
const ClusteredMarkers = ({ profiles, contacts, contactIds, onSelectProfile, selectedProfile, onShowTooltip, onHideTooltip }) => {
    const map = useMap();
    const [clusters, setClusters] = useState([]);
    const [zoom, setZoom] = useState(12);

    const clustererRef = useRef(null);
    const markersRef = useRef([]);

    // Create contact ID set for quick lookup (both peopleuuid and localuuid)
    const contactIdSet = useMemo(() => new Set(contactIds), [contactIds]);

    // Determine marker type for a profile
    // - 'cabaza-contact': Contact linked to Cabaza profile (has peopleuuid and is in contacts)
    // - 'private-contact': Local contact without Cabaza profile (no peopleuuid, marked as isPrivateContact)
    // - 'public': Cabaza public profile not in contacts
    const getMarkerType = useCallback((profile) => {
        const peopleuuid = profile.peopleuuid || profile.peopleUuid || profile.PEOPLEUUID;
        const localuuid = profile.localuuid || profile.LOCALUUID;

        // Check if it's a private contact (local contact without Cabaza profile)
        if (profile.isPrivateContact) {
            return 'private-contact';
        }

        // Check if it's in user's contacts
        const isInContacts = contactIdSet.has(peopleuuid) || contactIdSet.has(localuuid) || contacts.some(c =>
            (c.peopleuuid && c.peopleuuid === peopleuuid) ||
            (c.localuuid && c.localuuid === localuuid) ||
            (c.id && c.id === profile.id)
        );

        if (isInContacts && peopleuuid) {
            return 'cabaza-contact';
        }

        return 'public';
    }, [contactIdSet, contacts]);

    // Handle zoom changes
    useEffect(() => {
        if (!map) return;

        const listener = map.addListener('zoom_changed', () => {
            setZoom(map.getZoom() || 12);
        });

        return () => {
            if (listener) google.maps.event.removeListener(listener);
        };
    }, [map]);

    // Show individual markers when zoomed in enough
    const showIndividualMarkers = zoom >= 14;

    if (showIndividualMarkers) {
        return (
            <>
                {profiles.map((profile, idx) => {
                    const markerType = getMarkerType(profile);
                    return (
                        <CabazaMarker
                            key={profile.id || profile.peopleuuid || profile.localuuid || idx}
                            profile={profile}
                            markerType={markerType}
                            isSelected={selectedProfile?.id === profile.id || selectedProfile?.peopleuuid === profile.peopleuuid || selectedProfile?.localuuid === profile.localuuid}
                            onClick={() => onSelectProfile(profile, markerType !== 'public')}
                            onShowTooltip={onShowTooltip}
                            onHideTooltip={onHideTooltip}
                        />
                    );
                })}
            </>
        );
    }

    // For clustering at lower zoom levels, we'll use a simple grid-based clustering
    const clusteredData = useMemo(() => {
        if (showIndividualMarkers) return [];

        const gridSize = 0.05; // ~5km grid cells
        const grid = {};

        profiles.forEach(profile => {
            if (!profile.latitude || !profile.longitude) return;

            const lat = parseFloat(profile.latitude);
            const lng = parseFloat(profile.longitude);
            const cellKey = `${Math.floor(lat / gridSize)}_${Math.floor(lng / gridSize)}`;

            if (!grid[cellKey]) {
                grid[cellKey] = {
                    profiles: [],
                    centerLat: 0,
                    centerLng: 0
                };
            }
            grid[cellKey].profiles.push(profile);
            grid[cellKey].centerLat += lat;
            grid[cellKey].centerLng += lng;
        });

        return Object.values(grid).map(cell => ({
            count: cell.profiles.length,
            position: {
                lat: cell.centerLat / cell.profiles.length,
                lng: cell.centerLng / cell.profiles.length
            },
            profiles: cell.profiles
        }));
    }, [profiles, showIndividualMarkers]);

    return (
        <>
            {clusteredData.map((cluster, idx) => {
                if (cluster.count === 1) {
                    const profile = cluster.profiles[0];
                    const markerType = getMarkerType(profile);
                    return (
                        <CabazaMarker
                            key={`single-${idx}`}
                            profile={profile}
                            markerType={markerType}
                            isSelected={selectedProfile?.id === profile.id || selectedProfile?.localuuid === profile.localuuid}
                            onClick={() => onSelectProfile(profile, markerType !== 'public')}
                            onShowTooltip={onShowTooltip}
                            onHideTooltip={onHideTooltip}
                        />
                    );
                }
                return (
                    <AdvancedMarker
                        key={`cluster-${idx}`}
                        position={cluster.position}
                        onClick={() => {
                            if (map) {
                                map.panTo(cluster.position);
                                map.setZoom((map.getZoom() || 12) + 2);
                            }
                        }}
                    >
                        <ClusterMarker count={cluster.count} position={cluster.position} />
                    </AdvancedMarker>
                );
            })}
        </>
    );
};

/**
 * Map controls component
 */
const MapControls = ({ myPosition, t }) => {
    const map = useMap();

    const handleCenterOnMe = () => {
        if (map && myPosition) {
            map.panTo(myPosition);
            map.setZoom(14);
        }
    };

    return (
        <div className="absolute bottom-6 right-6 z-10">
            <button
                onClick={handleCenterOnMe}
                disabled={!myPosition}
                className="w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-green-500 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title={t.map?.centerOnMyLocation || 'Ma position'}
            >
                <Navigation size={24} />
            </button>
        </div>
    );
};

/**
 * Auto-center component - centers map on position once
 */
const AutoCenter = ({ position, zoom = 13 }) => {
    const map = useMap();
    const hasCenteredRef = useRef(false);
    const isDefaultCenter = position.lat === DEFAULT_CENTER.lat && position.lng === DEFAULT_CENTER.lng;

    useEffect(() => {
        // Only center if position is not the default (Brussels) and we haven't centered yet
        if (map && position && !hasCenteredRef.current && !isDefaultCenter) {
            map.panTo(position);
            map.setZoom(zoom);
            hasCenteredRef.current = true;
        }
    }, [map, position, zoom, isDefaultCenter]);

    return null;
};

/**
 * Search result marker (highlighted) - centers map on mount
 */
const SearchResultMarker = ({ contact, onClick }) => {
    const map = useMap();
    const initials = `${contact.firstName?.[0] || contact.firstname?.[0] || ''}${contact.lastName?.[0] || contact.lastname?.[0] || ''}`.toUpperCase() || '?';

    useEffect(() => {
        if (map && contact.latitude && contact.longitude) {
            map.panTo({ lat: parseFloat(contact.latitude), lng: parseFloat(contact.longitude) });
            map.setZoom(16);
        }
    }, [map, contact.latitude, contact.longitude]);

    return (
        <AdvancedMarker
            position={{ lat: parseFloat(contact.latitude), lng: parseFloat(contact.longitude) }}
            onClick={onClick}
        >
            <div className="relative cursor-pointer transform transition-transform hover:scale-110">
                <div className="absolute -inset-3 bg-cabaza-purple/30 rounded-full animate-pulse" />
                <div className="w-12 h-12 rounded-full bg-cabaza-purple text-white flex items-center justify-center font-semibold text-base shadow-lg border-3 border-white">
                    {initials}
                </div>
            </div>
        </AdvancedMarker>
    );
};

/**
 * Main Contacts Map Component
 */
const ContactsMap = () => {
    const { t } = useTranslation();
    const user = useMemo(() => getCurrentUser(), []);
    const userId = user?.id || user?.ID;
    const userUuid = user?.peopleUuid || user?.peopleuuid;

    const [allProfiles, setAllProfiles] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [contactIds, setContactIds] = useState([]);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [isSelectedContact, setIsSelectedContact] = useState(false);
    const [myPosition, setMyPosition] = useState(null);
    const [initialCenter, setInitialCenter] = useState(DEFAULT_CENTER);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchedProfile, setSearchedProfile] = useState(null);
    const [showSearchResults, setShowSearchResults] = useState(false);

    // Tooltip state
    const [tooltipData, setTooltipData] = useState(null);

    const searchTimeoutRef = useRef(null);

    // Get user's current position (GPS first, then profile coordinates as fallback)
    const initializePosition = useCallback(async () => {
        try {
            // Try GPS geolocation first
            const position = await getCurrentPosition();
            const pos = { lat: position.latitude, lng: position.longitude };
            setMyPosition(pos);
            setInitialCenter(pos);
        } catch (err) {
            console.warn('Could not get GPS position:', err.message);

            // Fallback: use logged-in user's profile coordinates
            if (userUuid) {
                try {
                    const userProfile = await getPeopleByUuid(userUuid);
                    const lat = userProfile?.latitude || userProfile?.LATITUDE;
                    const lng = userProfile?.longitude || userProfile?.LONGITUDE;

                    if (lat && lng) {
                        const pos = { lat: parseFloat(lat), lng: parseFloat(lng) };
                        setInitialCenter(pos);
                        console.log('Using user profile coordinates:', pos);
                    }
                } catch (profileErr) {
                    console.warn('Could not get user profile coordinates:', profileErr.message);
                }
            }
        }
    }, [userUuid]);

    // Load all public profiles and user's contacts
    const loadData = useCallback(async () => {
        try {
            setIsLoading(true);

            // Load all public profiles
            const publicProfiles = await getAllPeople({ limit: 1000 });

            // Filter profiles with valid coordinates or geocode them
            const profilesWithCoords = await Promise.all(
                publicProfiles.map(async (profile) => {
                    const lat = profile.latitude || profile.LATITUDE;
                    const lng = profile.longitude || profile.LONGITUDE;

                    if (lat && lng) {
                        return { ...profile, latitude: lat, longitude: lng };
                    }

                    const address = profile.googleAddress || profile.googleaddress || profile.GOOGLEADDRESS;
                    if (address) {
                        try {
                            const coords = await geocodeAddress(address);
                            return { ...profile, latitude: coords.latitude, longitude: coords.longitude };
                        } catch {
                            return null;
                        }
                    }
                    return null;
                })
            );

            const validProfiles = profilesWithCoords.filter(p => p && p.latitude && p.longitude);

            // Load user's contacts
            if (userId) {
                const allUserContacts = await getUserContacts(userId);

                // Filter out contacts with isPrivateProfile (profiles that are hidden/PRIVATE)
                const visibleContacts = allUserContacts.filter(c => !c.isPrivateProfile);
                setContacts(visibleContacts);

                // Build contact IDs set
                const ids = visibleContacts.map(c => c.peopleuuid || c.localuuid).filter(Boolean);
                setContactIds(ids);

                // Get private contacts (without peopleuuid) that have coordinates
                // These are local contacts not linked to Cabaza profiles
                const privateContactsWithCoords = await Promise.all(
                    visibleContacts
                        .filter(c => !c.peopleuuid && !c.peopleUuid) // Only contacts without Cabaza profile
                        .map(async (contact) => {
                            const lat = contact.latitude || contact.LATITUDE;
                            const lng = contact.longitude || contact.LONGITUDE;

                            if (lat && lng) {
                                return { ...contact, latitude: lat, longitude: lng, isPrivateContact: true };
                            }

                            // Try to geocode if address exists
                            const address = contact.googleAddress || contact.googleaddress || contact.GOOGLEADDRESS;
                            if (address) {
                                try {
                                    const coords = await geocodeAddress(address);
                                    return { ...contact, latitude: coords.latitude, longitude: coords.longitude, isPrivateContact: true };
                                } catch {
                                    return null;
                                }
                            }
                            return null;
                        })
                );

                // Combine Cabaza profiles with private contacts that have coordinates
                const validPrivateContacts = privateContactsWithCoords.filter(c => c && c.latitude && c.longitude);
                const combinedProfiles = [...validProfiles, ...validPrivateContacts];
                setAllProfiles(combinedProfiles);
            } else {
                setAllProfiles(validProfiles);
            }

        } catch (err) {
            console.error('Error loading data:', err);
            setError(t.map?.errorLoadingData || 'Erreur lors du chargement des données');
        } finally {
            setIsLoading(false);
        }
    }, [userId, t]);

    // Search profiles
    const searchProfiles = useCallback(async (query) => {
        if (!query || query.length < 2) {
            setSearchResults([]);
            setShowSearchResults(false);
            return;
        }

        setIsSearching(true);
        try {
            const results = await getAllPeople({ keyword: query, limit: 10 });

            const resultsWithCoords = await Promise.all(
                results.map(async (person) => {
                    const lat = person.latitude || person.LATITUDE;
                    const lng = person.longitude || person.LONGITUDE;

                    if (lat && lng) {
                        return { ...person, latitude: lat, longitude: lng };
                    }

                    const address = person.googleAddress || person.googleaddress || person.GOOGLEADDRESS;
                    if (address) {
                        try {
                            const coords = await geocodeAddress(address);
                            return { ...person, latitude: coords.latitude, longitude: coords.longitude };
                        } catch {
                            return null;
                        }
                    }
                    return null;
                })
            );

            setSearchResults(resultsWithCoords.filter(r => r !== null));
            setShowSearchResults(true);
        } catch (err) {
            console.error('Error searching profiles:', err);
        } finally {
            setIsSearching(false);
        }
    }, []);

    // Debounced search
    const handleSearchChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            searchProfiles(query);
        }, 300);
    };

    // Select search result
    const handleSelectSearchResult = (profile) => {
        const isContactProfile = contactIds.includes(profile.peopleuuid || profile.peopleUuid);
        setSearchedProfile(profile);
        setSelectedProfile(profile);
        setIsSelectedContact(isContactProfile);
        setShowSearchResults(false);
        setSearchQuery('');
    };

    // Select profile from map
    const handleSelectProfile = (profile, isContact) => {
        setSelectedProfile(profile);
        setIsSelectedContact(isContact);
        setSearchedProfile(null);
    };

    // Clear search
    const clearSearch = () => {
        setSearchQuery('');
        setSearchResults([]);
        setShowSearchResults(false);
        setSearchedProfile(null);
    };

    // Initialize
    useEffect(() => {
        initializePosition();
        loadData();
    }, [initializePosition, loadData]);

    // Stats
    const cabazaContactCount = contacts.filter(c => c.peopleuuid || c.peopleUuid).length;
    const privateContactCount = allProfiles.filter(p => p.isPrivateContact).length;
    const publicCount = allProfiles.filter(p => !p.isPrivateContact).length - cabazaContactCount;

    if (!GOOGLE_MAPS_API_KEY) {
        return (
            <div className="flex items-center justify-center h-full bg-gray-100">
                <div className="text-center p-8">
                    <MapPin size={48} className="mx-auto text-gray-400 mb-4" />
                    <h2 className="text-xl font-semibold text-gray-700 mb-2">
                        {t.map?.apiKeyError || 'Clé API Google Maps manquante'}
                    </h2>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full relative">
            <Map
                defaultCenter={initialCenter}
                defaultZoom={12}
                mapId="contacts-map"
                gestureHandling="greedy"
                disableDefaultUI={true}
                zoomControl={true}
                zoomControlOptions={{ position: 3 }}
                fullscreenControl={false}
                streetViewControl={false}
                mapTypeControl={false}
                className="w-full h-full"
            >
                    {/* Auto-center on user's position (GPS) or profile coordinates */}
                    <AutoCenter position={myPosition || initialCenter} zoom={myPosition ? 14 : 13} />

                    {/* My position */}
                    {myPosition && <MyPositionMarker position={myPosition} />}

                    {/* All profiles with clustering */}
                    <ClusteredMarkers
                        profiles={allProfiles}
                        contacts={contacts}
                        contactIds={contactIds}
                        onSelectProfile={handleSelectProfile}
                        selectedProfile={selectedProfile}
                        onShowTooltip={setTooltipData}
                        onHideTooltip={() => setTooltipData(null)}
                    />

                    {/* Search result marker (highlighted) */}
                    {searchedProfile && (
                        <SearchResultMarker
                            contact={searchedProfile}
                            onClick={() => handleSelectProfile(searchedProfile, contactIds.includes(searchedProfile.peopleuuid))}
                        />
                    )}

                    {/* Selected profile info window */}
                    {selectedProfile && selectedProfile.latitude && (
                        <ProfileInfoWindow
                            profile={selectedProfile}
                            isContact={isSelectedContact}
                            onClose={() => setSelectedProfile(null)}
                        />
                    )}

                {/* Map controls */}
                <MapControls myPosition={myPosition} t={t} />
            </Map>

            {/* Tooltip for name on hover */}
            {tooltipData && <NameTooltip {...tooltipData} />}

            {/* Search bar */}
            <div className="absolute top-4 left-1/2 -translate-x-1/2 w-full max-w-md px-4 z-10">
                <div className="relative">
                    <div className="flex items-center bg-white rounded-full shadow-lg overflow-hidden">
                        <div className="pl-4">
                            {isSearching ? (
                                <Loader2 size={20} className="text-gray-400 animate-spin" />
                            ) : (
                                <Search size={20} className="text-gray-400" />
                            )}
                        </div>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            placeholder={t.map?.searchPlaceholder || "Rechercher un profil Cabaza..."}
                            className="flex-1 px-4 py-3 outline-none text-gray-700"
                        />
                        {searchQuery && (
                            <button
                                onClick={clearSearch}
                                className="pr-4 text-gray-400 hover:text-gray-600"
                            >
                                <X size={20} />
                            </button>
                        )}
                    </div>

                    {/* Search results dropdown */}
                    {showSearchResults && searchResults.length > 0 && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg max-h-80 overflow-y-auto">
                            {searchResults.map((result, idx) => {
                                const isContactResult = contactIds.includes(result.peopleuuid || result.peopleUuid);
                                return (
                                    <button
                                        key={result.id || idx}
                                        onClick={() => handleSelectSearchResult(result)}
                                        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-100 last:border-b-0"
                                    >
                                        <div
                                            className="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-white"
                                            style={{ backgroundColor: isContactResult ? '#8b5cf6' : '#3b82f6' }}
                                        >
                                            {`${result.firstName?.[0] || result.firstname?.[0] || ''}${result.lastName?.[0] || result.lastname?.[0] || ''}`}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-900 truncate">
                                                {result.firstName || result.firstname} {result.lastName || result.lastname}
                                            </p>
                                            <p className="text-xs text-gray-500 truncate">
                                                {result.company || result.googleAddress || result.googleaddress}
                                            </p>
                                        </div>
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${isContactResult ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                            {isContactResult ? 'Contact' : 'Public'}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* No results */}
                    {showSearchResults && searchResults.length === 0 && searchQuery.length >= 2 && !isSearching && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg p-4 text-center text-gray-500">
                            {t.map?.noResults || "Aucun profil trouvé"}
                        </div>
                    )}
                </div>
            </div>

            {/* Legend */}
            <div className="absolute bottom-6 left-6 z-10 flex flex-col gap-2">
                <div className="px-4 py-2 bg-white rounded-lg shadow-lg flex flex-wrap items-center gap-3 text-sm">
                    <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#8b5cf6' }} />
                        <span className="text-gray-700">{cabazaContactCount} {t.map?.cabazaContacts || 'contacts Cabaza'}</span>
                    </div>
                    {privateContactCount > 0 && (
                        <>
                            <div className="w-px h-4 bg-gray-300" />
                            <div className="flex items-center gap-1.5">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#6b7280' }} />
                                <span className="text-gray-700">{privateContactCount} {t.map?.privateContacts || 'contacts privés'}</span>
                            </div>
                        </>
                    )}
                    <div className="w-px h-4 bg-gray-300" />
                    <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#3b82f6' }} />
                        <span className="text-gray-700">{publicCount} {t.map?.publicProfiles || 'profils publics'}</span>
                    </div>
                </div>
            </div>

            {/* Loading overlay */}
            {isLoading && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-20">
                    <div className="flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-cabaza-purple border-t-transparent rounded-full animate-spin" />
                        <p className="text-gray-600">{t.map?.loadingMap || 'Chargement de la carte...'}</p>
                    </div>
                </div>
            )}

            {/* Error message */}
            {error && (
                <div className="absolute top-20 left-1/2 -translate-x-1/2 px-4 py-2 bg-red-100 text-red-700 rounded-lg shadow-lg z-20">
                    {error}
                    <button
                        onClick={() => setError(null)}
                        className="ml-3 text-red-500 hover:text-red-700"
                    >
                        <X size={16} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default ContactsMap;
